import { useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * Injects tenant-specific tracking scripts (GA4, Meta Pixel, TikTok Pixel, etc.)
 * into the document <head>. Runs once when tenant config is loaded.
 */
export default function TenantTracking() {
  const { tenantConfig } = useTheme();
  const tracking = tenantConfig?.tracking;

  useEffect(() => {
    if (!tracking) return;
    const injected = [];

    // Google Analytics 4
    if (tracking.googleAnalyticsId) {
      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${tracking.googleAnalyticsId}`;
      document.head.appendChild(gaScript);
      injected.push(gaScript);

      const gaInit = document.createElement('script');
      gaInit.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${tracking.googleAnalyticsId}');
      `;
      document.head.appendChild(gaInit);
      injected.push(gaInit);
    }

    // Google Tag Manager
    if (tracking.googleTagManagerId) {
      const gtmScript = document.createElement('script');
      gtmScript.textContent = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${tracking.googleTagManagerId}');
      `;
      document.head.appendChild(gtmScript);
      injected.push(gtmScript);
    }

    // Meta (Facebook) Pixel
    if (tracking.metaPixelId) {
      const fbScript = document.createElement('script');
      fbScript.textContent = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${tracking.metaPixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(fbScript);
      injected.push(fbScript);
    }

    // TikTok Pixel
    if (tracking.tiktokPixelId && !window.__ttqLoaded) {
      window.__ttqLoaded = true;
      const ttScript = document.createElement('script');
      ttScript.textContent = `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
          ttq.load('${tracking.tiktokPixelId}');
          ttq.page();
        }(window, document, 'ttq');
      `;
      document.head.appendChild(ttScript);
      injected.push(ttScript);
    }

    // Snapchat Pixel
    if (tracking.snapchatPixelId) {
      const snapScript = document.createElement('script');
      snapScript.textContent = `
        (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
        {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
        a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
        r.src=n;var u=t.getElementsByTagName(s)[0];
        u.parentNode.insertBefore(r,u);})(window,document,
        'https://sc-static.net/scevent.min.js');
        snaptr('init', '${tracking.snapchatPixelId}', {});
        snaptr('track', 'PAGE_VIEW');
      `;
      document.head.appendChild(snapScript);
      injected.push(snapScript);
    }

    // Custom head scripts — only allow external script sources for safety
    if (tracking.customHeadScripts) {
      const allowedOrigins = [
        'https://www.googletagmanager.com',
        'https://connect.facebook.net',
        'https://analytics.tiktok.com',
        'https://sc-static.net',
        'https://snap.licdn.com',
        'https://www.clarity.ms',
      ];
      const customDiv = document.createElement('div');
      customDiv.innerHTML = tracking.customHeadScripts;
      const scripts = customDiv.querySelectorAll('script');
      scripts.forEach((origScript) => {
        if (origScript.src) {
          const isAllowed = allowedOrigins.some((o) => origScript.src.startsWith(o));
          if (!isAllowed) {
            console.warn('TenantTracking: blocked script from untrusted origin:', origScript.src);
            return;
          }
          const newScript = document.createElement('script');
          newScript.src = origScript.src;
          newScript.async = true;
          document.head.appendChild(newScript);
          injected.push(newScript);
        }
        // Inline scripts are not allowed for security
      });
    }

    // Cleanup on unmount (SPA navigation)
    return () => {
      injected.forEach((el) => {
        try { el.parentNode?.removeChild(el); } catch {}
      });
    };
  }, [tracking]);

  return null; // No visual output
}
